<template>
  <div class="mc-cost-table">
    <b-card>
      <div class="clearfix">
        <div class="d-flex mc-list-header">
          <div class="mc-list-header__title flex-grow-1 mc-general__label">
            {{ $t('costManagement.costDetail.title') }}
          </div>
          <McButton
            v-if="$route.params.id && selectedIdsLength"
            variant="secondary-red"
            class="mx-2 ms-auto"
            icon="icon-outline_trash"
            :text="$t('global.delete')"
            @click="$refs['mc-modal-delete'].show()"
          />

          <McButton
            v-if="
              $route.params.id &&
              status === 'Draf' &&
              getFeatureFlagData('inputDriverCostFlag').value
            "
            variant="secondary"
            class="me-2 ms-auto"
            text="Catatan Driver"
            icon="icon-outline_file-text"
            :disabled="disabledGlobal"
            v-b-modal.mc-modal-driver-cost
          />

          <McButton
            v-if="showApplyTemplateButton"
            variant="secondary"
            class="me-2 ms-auto"
            text="Template"
            icon="icon-outline_file-text"
            :disabled="disabledGlobal"
            @click="showTemplateConfirmation"
          />

          <McButton
            v-if="$route.params.id && status === 'Draf'"
            variant="primary"
            class="ms-auto"
            :text="$t('global.addField')"
            icon="icon-fill_plus"
            :disabled="disabledGlobal"
            @click="handleAdd"
          />
        </div>
      </div>
      <div v-if="!$route.params.id" class="d-flex justify-content-center">
        <div class="d-flex w-auto gap-3">
          <div>
            <img src="@/assets/images/no-data-illustration.png" width="150" alt />
          </div>
          <div class="d-flex flex-column justify-content-center gap-1">
            <strong class="primary-disabled-text">{{
              $t('costManagement.costDetail.noAdd')
            }}</strong>
            <p
              class="secondary-disabled-text"
              v-html="
                $t('costManagement.costDetail.saveDetailFirst', {
                  detail: '<strong>Detail</strong>',
                })
              "
            ></p>
          </div>
        </div>
      </div>
      <div v-else>
        <b-row align-h="end">
          <b-col md="8" xl="10">
            <h6 class="mc-row-check mt-0" v-if="selectedIdsLength">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              Baris terpilih.
              <a @click="handleUnselectAll" class="color-primary">Bersihkan pilihan</a>
            </h6>
            <McAlert
              v-if="invalidKeysLength && !selectedIdsLength"
              variant="error"
              text="Baris data error."
            >
              <template #prefix>
                <div class="mc-alert__custom_prefix">
                  <p>{{ invalidKeysLength }}</p>
                </div>
              </template>
            </McAlert>
          </b-col>
          <b-col md="4" xl="2">
            <!-- TODO: under consideration -->
            <b-input-group size="sm" class="mc-has-search d-none" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search"></i>
              </template>
              <b-form-input
                placeholder="Cari..."
                :disabled="disabledGlobal"
                :value="search"
                @input="handleSearchChange"
                ref="searchInput"
              />
              <template #append v-if="search">
                <button
                  class="clear-search-btn"
                  :disabled="disabledGlobal"
                  @click="handleClearSearch"
                >
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
          </b-col>
        </b-row>

        <FormulateForm
          v-model="formValues"
          name="editable-table-form"
          ref="formRef"
          :errors="formulateFormErrors"
          @submit="handleSubmit"
          @failed-validation="handleFailedValidation"
        >
          <div class="table-responsive border border-1 mc-cost-table__editable-table">
            <b-table
              :busy="isBusy"
              :items="items"
              :fields="computedFields"
              fixed
              show-empty
              foot-clone
              class="editable-table"
            >
              <!-- Loading -->
              <template #table-busy>
                <div class="__busy">
                  <b-spinner class="align-middle mb-2" />
                  <strong>{{ $t('global.text.loading') }}...</strong>
                </div>
              </template>

              <!-- Empty Result -->
              <template #empty>
                <div class="text-center my-4">{{ $t('global.text.noData') }}</div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    @change="handleSelectAll"
                    v-model="selectedAll"
                    :disabled="disabledGlobal"
                  />
                </div>
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div
                  class="d-flex"
                  @click="handleSortChange(data)"
                  :class="
                    data.field.name === 'amount' ? 'justify-content-end' : 'justify-content-between'
                  "
                >
                  {{ data.label }}
                  <template v-if="data.field.isSortable">
                    <div>
                      <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                        <i class="mc-icon icon-outline_chevron-up"></i>
                      </div>
                      <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                        <i class="mc-icon icon-outline_chevron-down"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    @change="(event) => handleSelect(event, data)"
                    :checked="selectedIds[data.item.id]"
                    :disabled="disabledGlobal"
                  />
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <!-- Editable, <Edit action> -->
                <template v-if="field.isEditable && isEditingRow == index">
                  <AttachmentField
                    v-if="field.name === 'file'"
                    ref="ref-attach-file"
                    :isSubmitting="isSubmitting"
                    :filename.sync="filename"
                  />
                  <FormulateInput
                    v-else
                    v-bind="{ ...field }"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :initialObject="
                      field.getInitialObject && field.name === 'activity_id'
                        ? field.getInitialObject(activityObj)
                        : undefined
                    "
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    validation-behavior="live"
                    error-behavior="value"
                    :validation-name="field.label"
                    size="sm"
                    :ref="`${field.name}-ref`"
                  />
                </template>

                <!-- Not Editable, Show Only Column, <Edit action> -->
                <template v-else-if="!field.isEditable && isEditingRow == index">
                  <div v-if="field.name === 'file'">{{ item?.attachments?.[0]?.name }}</div>
                  <div v-else>{{ showOnlyValue(value) }}</div>
                </template>

                <!-- Nothing happen action, the page is first loaded -->
                <template v-else>
                  <a
                    v-if="field.name === 'file'"
                    :href="item?.attachments?.[0]?.url"
                    target="_blank"
                  >
                    <!-- TODO: iterate attachments if more than one attachment feature is developed -->
                    <div class="mc-text-primary">{{ item?.attachments?.[0]?.name }}</div>
                  </a>
                  <div
                    v-else
                    :class="{
                      'td__invalid-data':
                        invalidItems[item.id] && invalidItems[item.id][field.name],
                    }"
                  >
                    {{
                      isCoaInInvalidItems(field, item)
                        ? invalidItems[item.id][field.name]
                        : transformCellValue(value, field)
                    }}
                  </div>
                </template>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div v-if="isEditingRow == data.index">
                  <b-button variant="outline" class="__accept-button btn" type="submit">
                    <i class="mc-icon icon-fill_checkmark"></i>
                  </b-button>

                  <b-button
                    variant="outline"
                    class="__cancel-button btn"
                    @click="handleEditCancel(data)"
                  >
                    <i class="mc-icon icon-outline_close"></i>
                  </b-button>
                </div>

                <b-button
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: isEditingRow !== null || isBusy,
                  }"
                  @click="handleEditStart(data)"
                >
                  <i class="mc-icon icon-outline_edit"></i>
                </b-button>
              </template>

              <template #foot()="{ column }">
                <div v-if="column === 'activity.name'" class="mc-footer">Total</div>
                <div v-if="column === 'amount'" class="mc-footer d-flex justify-content-end">
                  {{ totalAmount }}
                </div>
              </template>
            </b-table>
          </div>
        </FormulateForm>
      </div>

      <b-modal
        class="mc-modal"
        id="mc-modal-delete"
        ref="mc-modal-delete"
        title="Hapus"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            Apakah Anda yakin ingin menghapus <strong>{{ selectedIdsLength }}</strong> data berikut
            ?
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>

      <b-modal
        class="mc-modal"
        id="mc-modal-confirm-template"
        ref="mc-modal-confirm-template"
        :title="$t('costManagement.template.header')"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>{{ $t('costManagement.template.question') }}</p>
          <p>{{ $t('costManagement.template.info') }}</p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.no')"
              @click="cancel()"
            />
            <McButton
              variant="primary"
              :text="$t('global.action.yes')"
              @click="handleApplyTemplate"
              v-b-modal.mc-modal-apply-template
            />
          </div>
        </template>
      </b-modal>

      <ApplyTemplateModal @apply-template="applyTemplate" />
      <DriverCostModal @submit="submitDriverCostSelection" />
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from '@/helpers/debounce'
import { sum } from '@/helpers/calculate'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import * as CostItem from '@/services/rest-api/lib/cost-management/cost-item'
import * as FleetTaskCost from '@/services/rest-api/lib/cost-management/fleet-task-cost'
import AttachmentField from '../AttachmentField.vue'
import costFields from '../../lib/fields/costFields'
import { errorMessage } from '@/helpers/errorHandler'
import errorMessageList from '../../lib/constants/error-message-mapper'
import ApplyTemplateModal from '../ApplyTemplateModal/index.vue'
import DriverCostModal from '../DriverCostModal/index.vue'
import { Helper } from '@/mixin/helper'

export default {
  components: {
    McButton,
    McAlert,
    AttachmentField,
    ApplyTemplateModal,
    DriverCostModal,
  },
  mixins: [costFields],
  props: {
    apiClient: { required: true },
    status: { type: String },
    // TODO: under consideration
    limitOptions: {
      default: () => [5, 10, 25, 50, 100],
    },
    invalidFields: { type: Array },
    assignErrors: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      isBusy: true,
      isSubmitting: false,
      // TODO: under consideration <START>
      limit: this.limitOptions[0] ?? 10,
      page: 1,
      totalCount: 0,
      // TODO: under consideration <END>
      ids: [],
      items: [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},
      costDataValues: {},
      showResponse: {},
      activityObj: {},
      invalidItems: {},
      formulateFormErrors: {},
      filename: '',
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy || this.status === 'Dikonfirmasi'
    },
    computedFields() {
      let fields = [
        {
          key: 'checkbox',
          label: '',
          thStyle: 'width: 25px',
        },
        // Use the map method to create a new array from the costFields array
        ...this.costFields.map((field) => {
          // Use the spread operator to copy the field object
          return {
            ...field,
            // Use the ternary operator to assign the isEditable property
            isEditable:
              this.status === 'Dikonfirmasi' && field.name === 'activity_chart_of_account'
                ? true
                : this.status === 'Draf'
                ? field.isEditable
                : false,
          }
        }),
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]

      return fields
    },
    totalAmount() {
      let totalAmount = 0

      this.items.forEach((item) => {
        if (isNaN(item.amount)) return
        totalAmount = sum(totalAmount ?? 0, Number(item.amount))
      })

      return totalAmount.toLocaleString() ?? '-'
    },
    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
    showApplyTemplateButton() {
      return (
        this.$route.params.id &&
        this.status === 'Draf' &&
        this.hasAccess([
          'financial-record:create',
          'financial-record:read',
          'financial-record:update',
          'financial-record:delete',
        ])
      )
    },
  },
  watch: {
    async formValues() {
      if (this.isEditingRow == null && !this.invalidKeysLength) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(this.formValues, this.costDataValues)

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // delete the field in formulateFormErrors
        this.$delete(this.formulateFormErrors, field)
      })
    },
    'formValues.amount': function (newValue, oldValue) {
      if (this.isEditingRow === null) return

      // Clear amount value
      if (newValue == '' && !newValue) return

      // Same value
      if (newValue == oldValue) return

      // If user input valid quantity
      this.$set(this.items[this.isEditingRow], 'amount', newValue)
    },
  },
  async mounted() {
    await this.getData({
      item: true,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },

    handleFailedValidation() {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    showOnlyValue(value) {
      return value || '-'
    },
    transformCellValue(value) {
      if (value === false) return `-`
      if (typeof value === 'number') return `${value.toLocaleString()}`
      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    AssignInvalidErrors() {
      if (!this.invalidFields.length) return
      const indexObject = {}

      // format the invalidFields from array to object with id as the key
      for (const cost of this.invalidFields) {
        const costObj = {}

        for (const field in cost) {
          if (field === 'id') continue
          if (field === 'activity_chart_of_account') {
            costObj[field] = this.$t(errorMessageList[field][cost[field][0]])
            continue
          }
          costObj[field] = ['Data telah dihapus dari sistem.']
        }

        indexObject[cost.id] = costObj
      }

      // assign to invalidItems as state that could be used to show error
      this.invalidItems = indexObject
    },
    async getData(params) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      const data = await this.apiClient.get(this.$route.params.id, params)

      if (!data) return
      this.items = data
      this.totalCount = data.length
      this.ids = data.map((val) => val.id)
      this.isBusy = false
    },
    // TODO: under consideration
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    // TODO: under consideration
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    // TODO: under consideration
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(this.$route.params.id, data.item.id)
      if (!refetchedData) return

      const costData = refetchedData.data
      this.filename = costData.attachments?.[0] ? costData.attachments[0]?.name : ''

      // assign formValues with response show data
      this.costFields.forEach((field) => {
        if (!field.isEditable) return
        if (field.name === 'file') return
        let value

        // assign for the activity_id
        if (field.name === 'activity_id') value = costData.activity.id
        else value = costData[field.name]

        this.$set(this.formValues, field.name, value)
      })

      // assign activityObj with activity object in response show data
      this.activityObj = { ...costData.activity }

      this.isEditingRow = data.index
      this.showResponse = { ...costData }
      this.costDataValues = { ...this.formValues }

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[costData.id]) {
        this.formulateFormErrors = { ...this.invalidItems[costData.id] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formulateFormErrors = {}
      this.activityObj = {}

      this.isEditingRow = null
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (!createdData) return false

      const refetchedData = await this.apiClient.show(this.$route.params.id, createdData.data.id)
      if (!refetchedData) return false

      let detailData = refetchedData.data

      // Upload File
      const file = this.$refs['ref-attach-file'].file
      if (file) {
        const { errors } = await CostItem.uploadFile(this.$route.params.id, createdData.data.id, {
          type: 'PHOTO',
          file,
        })

        if (errors) {
          let message = 'Unggah gagal.'

          for (const [, value] of Object.entries(errors)) {
            message = value[0]
          }

          this.$mcToast.error(errorMessage(errorMessageList[message]))
          this.$refs['ref-attach-file'].resetInputFile()
          return
        }
        this.filename = ''
        const refetchedAfterUplad = await this.apiClient.show(
          this.$route.params.id,
          createdData.data.id
        )
        detailData = refetchedAfterUplad.data
      }

      this.ids.push(refetchedData.data.id)
      this.$mcToast.success('Data berhasil ditambahkan')

      return {
        ...data,
        ...detailData,
      }
    },
    async submitUpdate(data, oldData) {
      const file = this.$refs['ref-attach-file']?.file
      const [patchData, hasChanged] = this.filterUnchangedData(data, this.costDataValues)

      if (!hasChanged && !file) {
        this.isSubmitting = false
        this.handleEditCancel({ index: this.isEditingRow })
        return
      }

      let detailData

      if (hasChanged) {
        const updatedData = await this.apiClient.update(
          this.$route.params.id,
          oldData.id,
          patchData
        )
        if (!updatedData) return false

        const refetchedData = await this.apiClient.show(this.$route.params.id, oldData.id)
        if (!refetchedData) return false
        detailData = refetchedData.data

        if (updatedData.errors) {
          if (Helper.methods.isObject(updatedData.errors)) {
            await this.assignErrors(updatedData.errors)
            return {
              ...detailData,
            }
          }
        }
      }

      if (this.invalidItems[oldData.id]) {
        this.$delete(this.invalidItems, oldData.id)
        this.formulateFormErrors = {}
      }

      // Upload File
      if (file && this.status === 'Draf') {
        detailData = await this.updateFile(oldData)
      }

      this.$mcToast.success(this.$t('global.updateSuccess'))

      return {
        ...detailData,
      }
    },
    async updateFile(oldData) {
      let res
      const file = this.$refs['ref-attach-file'].file

      if (oldData.attachments?.[0]) {
        res = await CostItem.updateFile(
          this.$route.params.id,
          oldData.id,
          oldData.attachments?.[0].id,
          {
            file,
          }
        )
      } else {
        res = await CostItem.uploadFile(this.$route.params.id, oldData.id, { type: 'PHOTO', file })
      }

      if (res?.errors) {
        let message = 'Unggah gagal.'

        for (const [, value] of Object.entries(res.errors)) {
          message = value[0]
        }

        this.$mcToast.error(errorMessage(errorMessageList[message]))
        this.$refs['ref-attach-file'].resetInputFile()
        return
      }
      this.filename = ''
      const refetchedAfterUplad = await this.apiClient.show(this.$route.params.id, oldData.id)
      return refetchedAfterUplad.data
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formulateFormErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.costFields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }
      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.activityObj = {}
      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const promises = []
      let errorCaught = false
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key))
        }
      }
      const resArr = await Promise.all(promises)
      for (const key in resArr) {
        const { errors } = resArr[key]
        if (errors) {
          const errorMessage = Helper.methods.withErrorHandler({ errors }, errorMessageList)
          this.$mcToast.error(errorMessage)
          errorCaught = true
          break
        }
      }
      if (this.search) this.handleClearSearch()
      await this.getData({
        item: true,
      })

      // Delete data in invalid items
      for (const selectedid in this.selectedIds) {
        if (this.invalidItems[selectedid]) {
          this.$delete(this.invalidItems, selectedid)
        }
      }

      await this.$nextTick()
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      if (errorCaught) return
      this.$mcToast.success('Data berhasil dihapus')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [{ id: 0 }, ...this.items]
    },
    showTemplateConfirmation() {
      this.$refs['mc-modal-confirm-template'].show()
    },
    showDriverCostModal() {
      this.$refs['mc-modal-driver-cost'].show()
    },
    submitDriverCostSelection(selectedIds) {
      console.log('selected driver cost: ', selectedIds)
    },
    handleApplyTemplate() {
      this.$refs['mc-modal-confirm-template'].hide()
    },
    async applyTemplate(templateId) {
      const transactionId = this.$route.params.id

      this.isSubmitting = true
      const res = await CostItem.replaceByTemplate(transactionId, templateId)

      if (res.errors) {
        const errorMsg = errorMessageList[res.message] ?? 'Terjadi kesalahan.'
        return this.$mcToast.error(errorMsg)
      }

      const refetchItemData = await FleetTaskCost.show(transactionId)

      if (refetchItemData.errors) {
        const errorMsg = errorMessageList[res.message] ?? 'Terjadi kesalahan.'
        return this.$mcToast.error(errorMsg)
      }

      this.items = refetchItemData.data.transaction_item

      this.isSubmitting = false
      this.$mcToast.success('Template berhasil diterapkan.')
    },
    isCoaInInvalidItems(field, item) {
      return (
        field.name === 'activity_chart_of_account' &&
        this.invalidItems[item.id] &&
        this.invalidItems[item.id][field.name]
      )
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
